import api from "../api";
import type {
  SsnReadiness,
  SsnUpdaterBlocked,
  SsnUpdaterEstimate,
  SsnUpdaterRun,
} from "../adminUsers/adminUsers";

export interface PdfOptionSchemaProperty {
  type?: "string" | "boolean" | "integer" | "number";
  title?: string;
  description?: string;
  default?: unknown;
  format?: string;
  writeOnly?: boolean;
  minLength?: number;
}

/** JSON schema of a splitter's Pydantic options model (the job form). */
export interface PdfOptionsSchema {
  properties?: Record<string, PdfOptionSchemaProperty>;
  required?: string[];
}

export interface PdfSplitterInfo {
  key: string;
  label: string;
  /** The vendor whose PDFs it splits: a split major job of that vendor offers it. */
  provider: string;
  document: string;
  default_folder_name: string;
  /** Folders under the result that hold the employee folders ("W2", "1095"). */
  result_subfolders: string[];
  input_kinds: string[];
  options_schema: PdfOptionsSchema;
}

export interface PdfToolsConfig {
  splitters: PdfSplitterInfo[];
  providers: string[];
  limits: {
    max_file_bytes: number;
    max_files_per_job: number;
    max_total_bytes: number;
    max_concurrent_jobs: number;
  };
}

export type PdfJobStatus =
  | "pending"
  | "running"
  | "completed"
  | "failed"
  | "stopped"
  | "canceled";

export type PdfJobResultState = "" | "in_review" | "saving" | "saved" | "discarded";

export type PdfPublishStatus =
  | "none"
  | "pending"
  | "running"
  | "completed"
  | "failed";

export interface PdfJobProgress {
  phase: string;
  current_file: string;
  note: string;
  total_files: number;
  processed_files: number;
  total_pages: number;
  processed_pages: number;
}

export interface PdfJobSummaryTotals {
  files?: number;
  pages?: number;
  renamed?: number;
  error_pages?: number;
  skipped?: number;
  aborted_files?: number;
  employee_folders?: number;
}

export interface PdfJobPublishShort {
  status: PdfPublishStatus;
  destination: string;
  folder_name: string;
  error: string;
  published_at: string | null;
}

export interface PdfJobPublish extends PdfJobPublishShort {
  user_folder_id: number | null;
  /** What an upload job's Source folder should be: the save folder, or one
   * folder per form when the splitter sorts by form. */
  upload_source_folders: string[];
  /** The Data Storage folder the result was saved under. */
  folder_root: { prefix: string; display_name: string } | null;
}

export type SplitSsnUnlinkableReason =
  | "applied_for"
  | "control_number"
  | "unknown_name"
  | "no_last4"
  | "no_name_match"
  | "ambiguous"
  | "no_separator"
  | "other_tail";

/** The saved result checked against the vendor's SSN file (counts only). */
export interface SplitSsnCheck {
  status: "running" | "done" | "error";
  run_id?: string;
  trigger?: string | null;
  started_at?: string | null;
  checked_at?: string | null;
  algorithm?: "ssn" | "names";
  reason?: string;
  source_vendor?: string;
  destination?: string;
  roots?: { path: string; folders: number; loose_files: number }[];
  s3_folders?: number | null;
  duplicate_folders?: number;
  base_level_files?: number;
  unsplittable?: number;
  folder_id_shapes?: string[];
  updater_estimate?: SsnUpdaterEstimate | null;
  unlinkable?: Partial<Record<SplitSsnUnlinkableReason, number>>;
  updater_blocked?: SsnUpdaterBlocked | null;
  suggest_ssn_updater?: boolean;
  ssn_updater?: SplitSsnUpdaterSummary | null;
  error?: string | null;
}

export interface SplitSsnUpdaterSummary {
  status: "running" | "done" | "failed";
  /** running / interrupted (the run lost its worker) / done / failed. */
  state: "running" | "interrupted" | "done" | "failed" | null;
  added?: number | null;
  allow_similar?: boolean;
  started_at?: string | null;
  finished_at?: string | null;
  error?: string | null;
}

/** The short form list rows carry. */
export interface SplitSsnCheckSummary {
  status: "running" | "done" | "error";
  checked_at: string | null;
  algorithm?: "ssn" | "names";
  reason?: string;
  folders: number | null;
  linkable_exact: number | null;
  linkable_similar: number | null;
  already_linked: number | null;
  suggest_ssn_updater: boolean;
  updater_blocked: boolean;
  ssn_updater: SplitSsnUpdaterSummary | null;
  error: string | null;
}

export interface PdfJobMajorJobRef {
  id: number;
  name: string;
  vendor: string;
}

export interface PdfJobBase {
  job_id: string;
  status: PdfJobStatus;
  result_state: PdfJobResultState;
  splitter_key: string;
  splitter_label: string;
  provider: string;
  document: string;
  user: { id: number | null; username: string };
  major_job: PdfJobMajorJobRef | null;
  created_by: { id: number | null; username: string };
  files_count: number;
  input_names: string[];
  progress: PdfJobProgress;
  totals: PdfJobSummaryTotals;
  error_pages: number;
  has_results_zip: boolean;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  updated_at: string;
}

export interface PdfJobListItem extends PdfJobBase {
  publish: PdfJobPublishShort;
  ssn_check: SplitSsnCheckSummary | null;
}

export interface PdfJobFileSummary {
  file_name: string;
  pages: number;
  renamed: number;
  error_pages: number;
  skipped: number;
  aborted: boolean;
  abort_reason: string | null;
}

export interface PdfJobDetail extends PdfJobBase {
  options: Record<string, unknown>;
  inputs: { file_name: string; size: number | null; kind: string }[];
  summary: {
    totals: PdfJobSummaryTotals;
    per_file: PdfJobFileSummary[];
  } | null;
  error_message: string;
  publish: PdfJobPublish;
  save: {
    /** users/{id}/{vendor}/data/ - Save writes {data_root}{folder_name}/. */
    data_root: string;
    default_folder_name: string;
    result_subfolders: string[];
  };
  ssn_check: SplitSsnCheck | null;
  discarded_at: string | null;
}

export interface PdfJobListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: PdfJobListItem[];
}

export interface PdfUploadPresignResponse {
  upload_url: string;
  s3_key: string;
  s3_bucket: string;
  file_name: string;
  headers: Record<string, string>;
}

export interface PdfUploadFinalizeResponse {
  s3_key: string;
  file_name: string;
  size: number;
  kind: "zip" | "pdf";
}

export interface CreatePdfJobRequest {
  /** The split major job the job starts in; its user is the client. */
  major_job_id: number;
  user_id: number;
  splitter_key: string;
  options: Record<string, unknown>;
  inputs: { s3_key: string; file_name: string }[];
}

/** A running split job named by a 429 at the global concurrency limit. */
export interface PdfActiveJobRef {
  job_id: string;
  status: PdfJobStatus;
  phase: string;
  started_at: string | null;
  splitter_label: string;
  user: { id: number | null; username: string };
  major_job: { id: number; name: string } | null;
  created_by: string;
}

export type PdfWorkspaceSection = "input" | "result" | "error_pages";

export interface PdfWorkspaceItem {
  type: "folder" | "file";
  name: string;
  path: string;
  key?: string;
  size?: number;
}

export interface PdfWorkspaceListing {
  section: PdfWorkspaceSection;
  prefix: string;
  items: PdfWorkspaceItem[];
  next_cursor: string | null;
}

export interface PdfJobLogEvent {
  timestamp: number | null;
  message: string;
}

export interface PdfJobLogsResponse {
  source: "cloudwatch" | "file";
  events: PdfJobLogEvent[];
  next_token: string | null;
}

export interface SplitSsnUpdaterStatus {
  job_id: string;
  readiness: (SsnReadiness & { updater_blocked?: SsnUpdaterBlocked | null }) | null;
  ssn_check: SplitSsnCheck | null;
  run: SsnUpdaterRun | null;
}

const BASE = "/pdf-tools";

const jobTags = (jobId: string) => [
  { type: "PdfSplitJobs" as const, id: jobId },
  { type: "PdfSplitJobs" as const, id: "LIST" },
];

export const pdfToolsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getPdfToolsConfig: builder.query<PdfToolsConfig, void>({
      query: () => ({ url: `${BASE}/config/` }),
      keepUnusedDataFor: 3600,
    }),

    presignPdfUpload: builder.mutation<
      PdfUploadPresignResponse,
      { file_name: string; content_type: string; user_id: number }
    >({
      query: (body) => ({
        url: `${BASE}/uploads/presign/`,
        method: "POST",
        body,
      }),
    }),

    finalizePdfUpload: builder.mutation<
      PdfUploadFinalizeResponse,
      { s3_key: string; user_id: number }
    >({
      query: (body) => ({
        url: `${BASE}/uploads/finalize/`,
        method: "POST",
        body,
      }),
    }),

    createPdfJob: builder.mutation<PdfJobDetail, CreatePdfJobRequest>({
      query: (body) => ({
        url: `${BASE}/jobs/`,
        method: "POST",
        body,
      }),
      // The major jobs list counts split jobs.
      invalidatesTags: (_r, _e, { user_id }) => [
        { type: "PdfSplitJobs", id: "LIST" },
        { type: "MajorJobs", id: user_id },
      ],
    }),

    getPdfJobs: builder.query<
      PdfJobListResponse,
      {
        page?: number;
        page_size?: number;
        user_id?: number | null;
        major_job_id?: number | null;
        status?: string | null;
      }
    >({
      query: ({ page, page_size, user_id, major_job_id, status }) => ({
        url: `${BASE}/jobs/`,
        params: {
          page: page ?? 1,
          page_size: page_size ?? 25,
          user_id: user_id || undefined,
          major_job_id: major_job_id || undefined,
          status: status || undefined,
        },
      }),
      providesTags: [{ type: "PdfSplitJobs", id: "LIST" }],
    }),

    getPdfJob: builder.query<PdfJobDetail, string>({
      query: (jobId) => ({ url: `${BASE}/jobs/${jobId}/` }),
      providesTags: (_r, _e, jobId) => [{ type: "PdfSplitJobs", id: jobId }],
    }),

    getPdfJobFiles: builder.query<
      PdfWorkspaceListing,
      {
        jobId: string;
        section: PdfWorkspaceSection;
        prefix?: string;
        cursor?: string | null;
        limit?: number;
      }
    >({
      query: ({ jobId, section, prefix, cursor, limit }) => ({
        url: `${BASE}/jobs/${jobId}/files/`,
        params: {
          section,
          prefix: prefix || undefined,
          cursor: cursor || undefined,
          limit: limit ?? 100,
        },
      }),
    }),

    // A read: a query, so Client View may open files too. Never cached - a
    // presigned link expires.
    getPdfJobFileUrl: builder.query<
      { url: string; file_name: string },
      { jobId: string; key: string }
    >({
      query: ({ jobId, key }) => ({
        url: `${BASE}/jobs/${jobId}/file/`,
        params: { key },
      }),
      keepUnusedDataFor: 0,
    }),

    getPdfJobLogs: builder.query<
      PdfJobLogsResponse,
      { jobId: string; next_token?: string | null }
    >({
      query: ({ jobId, next_token }) => ({
        url: `${BASE}/jobs/${jobId}/logs/`,
        params: { next_token: next_token || undefined },
      }),
    }),

    getPdfJobDownload: builder.query<
      { download_url: string; filename: string },
      string
    >({
      query: (jobId) => ({ url: `${BASE}/jobs/${jobId}/download/` }),
      keepUnusedDataFor: 0,
    }),

    savePdfJob: builder.mutation<
      PdfJobDetail,
      { jobId: string; folder_name: string; overwrite?: boolean }
    >({
      query: ({ jobId, ...body }) => ({
        url: `${BASE}/jobs/${jobId}/save/`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { jobId }) => jobTags(jobId),
    }),

    discardPdfJob: builder.mutation<
      PdfJobDetail,
      {
        jobId: string;
        userId: number | null;
        delete_data: boolean;
        delete_saved_data: boolean;
      }
    >({
      query: ({ jobId, delete_data, delete_saved_data }) => ({
        url: `${BASE}/jobs/${jobId}/discard/`,
        method: "POST",
        body: { delete_data, delete_saved_data },
      }),
      invalidatesTags: (_r, _e, { jobId, userId }) => [
        ...jobTags(jobId),
        ...(userId != null ? [{ type: "MajorJobs" as const, id: userId }] : []),
      ],
    }),

    stopPdfJob: builder.mutation<PdfJobDetail, string>({
      query: (jobId) => ({
        url: `${BASE}/jobs/${jobId}/stop/`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, jobId) => jobTags(jobId),
    }),

    restartPdfJob: builder.mutation<
      PdfJobDetail,
      { jobId: string; mode: "resume" | "fresh" }
    >({
      query: ({ jobId, mode }) => ({
        url: `${BASE}/jobs/${jobId}/restart/`,
        method: "POST",
        body: { mode },
      }),
      invalidatesTags: (_r, _e, { jobId }) => jobTags(jobId),
    }),

    // CAL-5987: the saved result against the vendor's SSN file, and SSN Updater.
    runPdfJobSsnCheck: builder.mutation<
      { job_id: string; ssn_check: SplitSsnCheck | null },
      string
    >({
      query: (jobId) => ({
        url: `${BASE}/jobs/${jobId}/ssn-check/`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, jobId) => jobTags(jobId),
    }),

    getPdfJobSsnUpdater: builder.query<
      SplitSsnUpdaterStatus,
      { jobId: string; includeEntries?: boolean }
    >({
      query: ({ jobId, includeEntries }) => ({
        url: `${BASE}/jobs/${jobId}/ssn-updater/`,
        params: { include_entries: includeEntries ? 1 : undefined },
      }),
      // Refreshed with its job: a run ends with a re-check the dialog waits for.
      providesTags: (_r, _e, { jobId }) => [{ type: "PdfSplitJobs", id: jobId }],
    }),

    startPdfJobSsnUpdater: builder.mutation<
      { run: SsnUpdaterRun; queued: boolean },
      { jobId: string; allowSimilar: boolean }
    >({
      query: ({ jobId, allowSimilar }) => ({
        url: `${BASE}/jobs/${jobId}/ssn-updater/`,
        method: "POST",
        body: { allow_similar: allowSimilar },
      }),
      invalidatesTags: (_r, _e, { jobId }) => jobTags(jobId),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPdfToolsConfigQuery,
  usePresignPdfUploadMutation,
  useFinalizePdfUploadMutation,
  useCreatePdfJobMutation,
  useGetPdfJobsQuery,
  useGetPdfJobQuery,
  useLazyGetPdfJobQuery,
  useGetPdfJobFilesQuery,
  useLazyGetPdfJobFilesQuery,
  useLazyGetPdfJobFileUrlQuery,
  useGetPdfJobLogsQuery,
  useLazyGetPdfJobLogsQuery,
  useLazyGetPdfJobDownloadQuery,
  useSavePdfJobMutation,
  useDiscardPdfJobMutation,
  useStopPdfJobMutation,
  useRestartPdfJobMutation,
  useRunPdfJobSsnCheckMutation,
  useGetPdfJobSsnUpdaterQuery,
  useStartPdfJobSsnUpdaterMutation,
} = pdfToolsApi;
